import { ARENA_CRATE_PAIRS, CRATE_RADIUS, PILLAR_RADIUS_LARGE, PILLAR_RADIUS_SMALL } from './constants';
import { isFree, tilePassable } from './obstacles';
import { FLOOR, WALL, circleFits, tileAt, tileCenter } from './tilemap';
import type { Pillar, TileMap, Vec2, WeaponSpot, WeaponTier } from './types';
import { dist } from './vec';

/**
 * ASCII legend of the arena maps (derived from RunAndMove's parseLevel.ts):
 *   '#' wall, '.' floor, 'C' crate, '1' / '2' / '3' weapon quality marker
 *   (WeaponSpec.tier), 'A' / 'B' start of player 0 / 1, 'O' / 'o' centre of a large / small round
 *   pillar (PILLAR_RADIUS_LARGE / _SMALL). The tiles a pillar reaches over stay '.' in the art; the
 *   circle itself is what blocks.
 */
export type LevelChar = '#' | '.' | 'C' | '1' | '2' | '3' | 'A' | 'B' | 'O' | 'o';

export interface LevelOptions {
  /** crates scattered at random when a match starts, as point-symmetric pairs; default 0 */
  cratePairs?: number;
}

export interface ParsedLevel {
  /** walls and floor only; crates, spots, starts and pillar centres are floor here */
  map: TileMap;
  /** world positions (tile centres) */
  starts: [Vec2, Vec2];
  /** the fixed crates of the art ('C'); scattered ones are drawn per match from cratePairs */
  crates: Vec2[];
  weaponSpots: WeaponSpot[];
  pillars: Pillar[];
  cratePairs: number;
}

export class LevelError extends Error {
  constructor(message: string) {
    super(`invalid level: ${message}`);
    this.name = 'LevelError';
  }
}

interface Tile {
  x: number;
  y: number;
}

const DIRS4 = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

function tileName(p: Vec2): string {
  const t = tileAt(p);
  return `${t.x},${t.y}`;
}

/**
 * Parses an ASCII map. Throws a LevelError describing the first problem found: maps are hand-made,
 * so loud failure beats a half-broken arena.
 */
export function parseLevel(rows: readonly string[], options: LevelOptions = {}): ParsedLevel {
  if (rows.length < 5) throw new LevelError('map needs at least 5 rows');
  const width = rows[0].length;
  const height = rows.length;
  if (width < 5) throw new LevelError('map needs at least 5 columns');
  const tiles: string[] = [];
  const startTiles: (Tile | undefined)[] = [undefined, undefined];
  const crateTiles: Tile[] = [];
  const spotTiles: (Tile & { tier: WeaponTier })[] = [];
  const pillars: Pillar[] = [];
  for (let y = 0; y < height; y++) {
    const row = rows[y];
    if (row.length !== width) throw new LevelError(`row ${y} has length ${row.length}, expected ${width}`);
    for (let x = 0; x < width; x++) {
      const ch = row[x];
      if (ch === WALL) {
        tiles.push(WALL);
        continue;
      }
      const border = x === 0 || y === 0 || x === width - 1 || y === height - 1;
      if (border) throw new LevelError(`border tile at ${x},${y} must be a wall, got '${ch}'`);
      tiles.push(FLOOR);
      switch (ch) {
        case FLOOR:
          break;
        case 'C':
          crateTiles.push({ x, y });
          break;
        case '1':
        case '2':
        case '3':
          spotTiles.push({ x, y, tier: Number(ch) as WeaponTier });
          break;
        case 'O':
        case 'o':
          pillars.push({ pos: tileCenter(x, y), radius: ch === 'O' ? PILLAR_RADIUS_LARGE : PILLAR_RADIUS_SMALL });
          break;
        case 'A':
        case 'B': {
          const id = ch === 'A' ? 0 : 1;
          if (startTiles[id]) throw new LevelError(`start '${ch}' appears twice`);
          startTiles[id] = { x, y };
          break;
        }
        default:
          throw new LevelError(`unknown tile '${ch}' at ${x},${y}`);
      }
    }
  }
  const [a, b] = startTiles;
  if (!a) throw new LevelError("missing start 'A'");
  if (!b) throw new LevelError("missing start 'B'");
  if (spotTiles.length === 0) throw new LevelError("no weapon spot ('1', '2' or '3')");
  const cratePairs = options.cratePairs ?? 0;
  if (!Number.isInteger(cratePairs) || cratePairs < 0) throw new LevelError('cratePairs must be a non-negative integer');

  const map: TileMap = { width, height, tiles: tiles.join('') };

  for (let i = 0; i < pillars.length; i++) {
    const p = pillars[i];
    if (!circleFits(map, p.pos.x, p.pos.y, p.radius)) throw new LevelError(`pillar at ${tileName(p.pos)} reaches into a wall`);
    for (let j = 0; j < i; j++) {
      const q = pillars[j];
      if (dist(p.pos, q.pos) < p.radius + q.radius) throw new LevelError(`pillars at ${tileName(q.pos)} and ${tileName(p.pos)} overlap`);
    }
  }
  const blocked = (t: Tile): boolean => !tilePassable(map, pillars, t.x, t.y);
  if (blocked(a)) throw new LevelError(`start 'A' at ${a.x},${a.y} is blocked by a pillar`);
  if (blocked(b)) throw new LevelError(`start 'B' at ${b.x},${b.y} is blocked by a pillar`);
  for (const t of spotTiles) if (blocked(t)) throw new LevelError(`weapon spot at ${t.x},${t.y} is blocked by a pillar`);
  for (const t of crateTiles) {
    const c = tileCenter(t.x, t.y);
    if (!isFree(map, pillars, c.x, c.y, CRATE_RADIUS)) throw new LevelError(`crate at ${t.x},${t.y} is blocked by a pillar`);
  }

  // Reachability over passable tiles from start A: B and every weapon spot must be reachable. Crates
  // count as passable because they can be pushed away.
  const seen = new Uint8Array(width * height);
  const queue: Tile[] = [a];
  seen[a.y * width + a.x] = 1;
  while (queue.length) {
    const t = queue.shift()!;
    for (const [dx, dy] of DIRS4) {
      const nx = t.x + dx;
      const ny = t.y + dy;
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
      const i = ny * width + nx;
      if (seen[i] || !tilePassable(map, pillars, nx, ny)) continue;
      seen[i] = 1;
      queue.push({ x: nx, y: ny });
    }
  }
  if (!seen[b.y * width + b.x]) throw new LevelError("start 'B' is not reachable from 'A'");
  for (const t of spotTiles) if (!seen[t.y * width + t.x]) throw new LevelError(`weapon spot at ${t.x},${t.y} is not reachable`);

  const centre = (t: Tile): Vec2 => tileCenter(t.x, t.y);
  return { map, starts: [centre(a), centre(b)], crates: crateTiles.map(centre), weaponSpots: spotTiles.map((t) => ({ pos: centre(t), tier: t.tier })), pillars, cratePairs };
}

/**
 * The v1 arena, 25x19: four large pillars towards the corners, four small ones in a square around
 * the centre, the starts in opposite corners. The eight quality markers lie in three zones, from the
 * edge inwards: tier 1 outside the rectangle of the large pillars, tier 2 on that rectangle between
 * the large pillars, tier 3 inside the square of the small pillars, so strong weapons are most likely
 * in the most exposed place. Pickup positions are drawn freely from reachable floor. The map is
 * point-symmetric (tile (x, y) mirrors to (24 - x, 18 - y), A to B), so neither start has a structural
 * advantage; the crates are scattered per match as mirrored
 * pairs (ARENA_CRATE_PAIRS). Verified by levels.test.ts.
 */
export const ARENA_ROWS: readonly string[] = [
  '#########################',
  '#A......................#',
  '#.......................#',
  '#....................1..#',
  '#.......................#',
  '#....O......2......O....#',
  '#........o.....o........#',
  '#.......................#',
  '#.......................#',
  '#....2....3...3....2....#',
  '#.......................#',
  '#.......................#',
  '#........o.....o........#',
  '#....O......2......O....#',
  '#.......................#',
  '#..1....................#',
  '#.......................#',
  '#......................B#',
  '#########################',
];

export const ARENA: ParsedLevel = parseLevel(ARENA_ROWS, { cratePairs: ARENA_CRATE_PAIRS });
